import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Users, RefreshCw, PartyPopper } from 'lucide-react';
import { xboxApiService } from '@/services/xboxApi';

interface Friend {
  xuid?: string;
  gamertag: string;
  avatarUrl: string;
  isOnline: boolean;
  lastSeen?: string;
  gamerScore?: string;
  presenceText?: string;
  inParty?: boolean;
  preferredColor?: {
    primaryColor: string;
    secondaryColor: string;
    tertiaryColor: string;
  }
}

const Friends: React.FC = () => {
  const [friends, setFriends] = useState<Friend[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Keep latest friends for polling comparison without re-subscribing the interval
  const friendsRef = React.useRef<Friend[]>(friends);
  useEffect(() => {
    friendsRef.current = friends;
  }, [friends]);

  // Mock data for demonstration
  const mockFriends: Friend[] = [
    {
      gamertag: "BestFriend123",
      avatarUrl: "https://avatar-ssl.xboxlive.com/avatar/BestFriend123/avatarpic-l.png",
      isOnline: true
    },
    {
      gamertag: "GamingBuddy456",
      avatarUrl: "https://avatar-ssl.xboxlive.com/avatar/GamingBuddy456/avatarpic-l.png",
      isOnline: false,
      lastSeen: "2 hours ago"
    },
    {
      gamertag: "XboxMaster789",
      avatarUrl: "https://avatar-ssl.xboxlive.com/avatar/XboxMaster789/avatarpic-l.png",
      isOnline: true
    },
    {
      gamertag: "ProGamer2024",
      avatarUrl: "https://avatar-ssl.xboxlive.com/avatar/ProGamer2024/avatarpic-l.png",
      isOnline: false,
      lastSeen: "1 day ago"
    },
    {
      gamertag: "ElitePlayer",
      avatarUrl: "https://avatar-ssl.xboxlive.com/avatar/ElitePlayer/avatarpic-l.png",
      isOnline: true
    },
    {
      gamertag: "CasualGamer99",
      avatarUrl: "https://avatar-ssl.xboxlive.com/avatar/CasualGamer99/avatarpic-l.png",
      isOnline: false,
      lastSeen: "3 days ago"
    }
  ];

  const mapPeopleToFriends = (people: any[]): Friend[] => {
    const mapped: Friend[] = people.map((p: any) => {
      const presenceText: string | undefined = p.presenceText;
      const presenceIsLastSeen = typeof presenceText === 'string' && presenceText.toLowerCase().startsWith('last seen');
      return {
        xuid: p.xuid,
        gamertag: p.gamertag || p.displayName || p.preferredName || 'Unknown',
        avatarUrl: p.displayPicRaw || `https://avatar-ssl.xboxlive.com/avatar/${encodeURIComponent(p.gamertag || '')}/avatarpic-l.png`,
        isOnline: (p.presenceState || '').toLowerCase() === 'online',
        lastSeen: presenceIsLastSeen ? presenceText!.replace(/^last seen\s*/i, '') : (p.presenceDetails?.lastSeenDateTime || undefined),
        gamerScore: p.gamerScore,
        presenceText,
        inParty: !!p.multiplayerSummary && Number(p.multiplayerSummary.InParty) > 0,
        preferredColor: p.preferredColor,
      };
    });
    mapped.sort((a, b) => Number(b.isOnline) - Number(a.isOnline) || a.gamertag.localeCompare(b.gamertag));
    return mapped;
  };

  const handleFetchFriends = async () => {
    setIsLoading(true);
    try {
      // Try proxy endpoint first
      const res = await xboxApiService.getFriendsViaProxy();
      if (res.data && res.data.people) {
        const mapped: Friend[] = mapPeopleToFriends(res.data.people);
        setFriends(mapped);
        // cache
        localStorage.setItem('friends_cache', JSON.stringify(mapped));
      } else {
        // fallback to mock if structure unexpected
        setFriends(mockFriends);
      }
    } catch (error) {
      console.error('Failed to fetch friends:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Use cache if present
    const cached = localStorage.getItem('friends_cache');
    if (cached) {
      try {
        // Handle both legacy mapped cache and raw people payload
        const parsed = JSON.parse(cached);
        const list: Friend[] = Array.isArray(parsed) && parsed.length && parsed[0]?.gamertag && parsed[0]?.isOnline !== undefined
          ? (parsed as Friend[])
          : mapPeopleToFriends(parsed as any[]);
        setFriends(list);
        // If cached entries lack preferredColor, refresh to enrich cache
        const missingColors = list.some(f => !f.preferredColor);
        if (missingColors) {
          handleFetchFriends();
        }
      } catch {}
    } else {
      handleFetchFriends();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cache sync every 10s without causing new API calls (Layout handles API polling)
  useEffect(() => {
    let intervalId: number | undefined;
    const cacheHashRef = { current: '' } as React.MutableRefObject<string>;
    const syncFromCache = () => {
      const raw = localStorage.getItem('friends_cache') || '';
      if (raw && raw !== cacheHashRef.current) {
        try {
          const parsed = JSON.parse(raw);
          const list = Array.isArray(parsed) && parsed.length && parsed[0]?.gamertag && parsed[0]?.isOnline !== undefined
            ? (parsed as Friend[])
            : mapPeopleToFriends(parsed as any[]);
          setFriends(list);
          cacheHashRef.current = raw;
        } catch {}
      }
    };
    syncFromCache();
    intervalId = window.setInterval(syncFromCache, 10000);
    return () => {
      if (intervalId !== undefined) {
        clearInterval(intervalId);
      }
    };
  }, []);

  const getInitials = (gamertag: string) => {
    return gamertag.slice(0, 2).toUpperCase();
  };

  const renderFriendCard = (friend: Friend, index: number) => {
    const presenceIsLastSeen = (friend.presenceText || '').toLowerCase().startsWith('last seen');
    const toHex = (c?: string) => (c ? (c.startsWith('#') ? c : `#${c}`) : undefined);
    const withAlpha = (hex?: string, alphaHex: string = '33') => (hex ? `${hex}${alphaHex}` : undefined);
    const pc = toHex(friend.preferredColor?.primaryColor);
    const sc = toHex(friend.preferredColor?.secondaryColor);
    const bgA = withAlpha(pc, '14');
    const bgB = withAlpha(sc, '14');
    return (
      <div
        key={index}
        className="relative overflow-hidden flex items-center space-x-4 rounded-lg border p-4 transition-colors"
        style={{
          borderColor: pc || undefined,
          background: bgA && bgB ? `linear-gradient(90deg, ${bgA}, ${bgB})` : undefined,
        }}
      >
        {pc && (
          <span
            className="absolute left-0 top-0 h-full w-1.5"
            style={{ backgroundColor: pc }}
          />
        )}
        <div className="relative">
          <Avatar className="h-12 w-12">
            <AvatarImage 
              src={friend.avatarUrl} 
              alt={`${friend.gamertag}'s avatar`}
            />
            <AvatarFallback className="bg-primary text-primary-foreground">
              {getInitials(friend.gamertag)}
            </AvatarFallback>
          </Avatar>
          {friend.isOnline && (
            <div className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full bg-green-500 border-2 border-background"></div>
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <p className="font-medium text-foreground truncate">
            {friend.gamertag}
          </p>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
            {friend.isOnline ? (
              <span className="text-green-500">Online</span>
            ) : presenceIsLastSeen ? (
              <span className="truncate" title={friend.presenceText}>{friend.presenceText}</span>
            ) : friend.lastSeen ? (
              <span>Last seen {friend.lastSeen}</span>
            ) : (
              <span>Offline</span>
            )}
            {friend.isOnline && friend.presenceText && !presenceIsLastSeen && (
              <span className="truncate" title={friend.presenceText}>
                {friend.presenceText}
              </span>
            )}
            {friend.inParty && (
              <span className="inline-flex items-center gap-1 text-cyan-400">
                <PartyPopper className="h-3 w-3" /> Party
              </span>
            )}
          </div>
          {friend.gamerScore && (
            <p className="text-xs text-muted-foreground">Gamerscore: {friend.gamerScore}</p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">Friends</h1>
          <p className="text-muted-foreground mt-2">
            View your Xbox Live friends and their online status
          </p>
        </div>
        
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search friends..."
            className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:w-56"
          />
          <Button
            onClick={handleFetchFriends}
            disabled={isLoading}
            className="flex w-full items-center justify-center gap-2 sm:w-auto"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            {isLoading ? 'Loading...' : 'Fetch Friends'}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Friends List</CardTitle>
          <CardDescription>
            Your Xbox Live friends and their current status
          </CardDescription>
        </CardHeader>
        <CardContent>
          {friends.length > 0 ? (
            (() => {
              const filtered = friends.filter((f) => {
                if (!searchQuery.trim()) return true;
                const q = searchQuery.toLowerCase();
                const fields = [
                  f.gamertag,
                  f.presenceText || '',
                  f.gamerScore ? String(f.gamerScore) : '',
                  f.isOnline ? 'online' : 'offline',
                ].join(' ').toLowerCase();
                return fields.includes(q);
              });
              const onlineList = filtered.filter(f => f.isOnline);
              const offlineList = filtered.filter(f => !f.isOnline);
              return (
                <div className="space-y-6">
                  {onlineList.length > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-green-400">Online ({onlineList.length})</h3>
                      </div>
                      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {onlineList.map(renderFriendCard)}
                      </div>
                    </div>
                  )}
                  {offlineList.length > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-muted-foreground">Offline ({offlineList.length})</h3>
                      </div>
                      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {offlineList.map(renderFriendCard)}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()
          ) : (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              <div className="text-center">
                <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No friends found</p>
                <p className="text-sm">Click "Fetch Friends" to load your friends list</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {friends.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Statistics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
              <div className="text-center p-4 rounded-lg bg-accent/20">
                <div className="text-2xl font-bold text-foreground">{friends.length}</div>
                <div className="text-sm text-muted-foreground">Total Friends</div>
              </div>
              <div className="text-center p-4 rounded-lg bg-green-500/20">
                <div className="text-2xl font-bold text-green-500">
                  {friends.filter(f => f.isOnline).length}
                </div>
                <div className="text-sm text-muted-foreground">Online</div>
              </div>
              <div className="text-center p-4 rounded-lg bg-red-500/20">
                <div className="text-2xl font-bold text-red-500">
                  {friends.filter(f => !f.isOnline).length}
                </div>
                <div className="text-sm text-muted-foreground">Offline</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Friends;
